import * as React from 'react';
import { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField as MuiTextField,
  Tooltip,
  IconButton,
  LinearProgress,
} from '@mui/material';
import {
  Upload,
  SmartToy,
  ContentCopy,
  Clear,
  Help,
} from '@mui/icons-material';
import { TextInput } from 'react-admin';

interface TranscriptInputProps {
  source: string;
  label?: string;
  value?: string;
  onChange?: (value: string) => void;
  fullWidth?: boolean;
  multiline?: boolean;
  minRows?: number;
  maxRows?: number;
  helperText?: string;
}

export const TranscriptInput: React.FC<TranscriptInputProps> = ({
  source,
  label = 'Транскрибация урока',
  value = '',
  onChange,
  fullWidth = true,
  multiline = true,
  minRows = 5,
  maxRows = 15,
  helperText = 'Текстовая транскрибация урока для RAG системы',
}) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [importText, setImportText] = useState('');

  const handleClear = useCallback(() => {
    if (onChange) {
      onChange('');
    }
  }, [onChange]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
      // Можно добавить уведомление об успешном копировании
    } catch (err) {
      console.error('Ошибка копирования:', err);
    }
  }, [value]);

  const handleImport = useCallback(() => {
    if (onChange && importText.trim()) {
      onChange(importText);
      setImportText('');
      setOpenDialog(false);
    }
  }, [onChange, importText]);

  const handleAutoGenerate = useCallback(async () => {
    setIsGenerating(true);
    try {
      // Здесь будет интеграция с сервисом автоматической транскрибации
      // Пока что показываем заглушку
      if (onChange) {
        onChange(
          'Автоматически сгенерированная транскрибация будет доступна в будущих версиях...'
        );
      }
    } catch (error) {
      console.error('Ошибка генерации транскрибации:', error);
    } finally {
      setIsGenerating(false);
    }
  }, [onChange]);

  return (
    <Box sx={{ width: '100%' }}>
      {/* <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
          {label}
        </Typography>
        <Tooltip title="Помощь по работе с транскрибацией">
          <IconButton size="small">
            <Help />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Tooltip title="Импортировать транскрибацию из текста">
          <Button
            variant="outlined"
            size="small"
            startIcon={<Upload />}
            onClick={() => setOpenDialog(true)}
          >
            Импорт
          </Button>
        </Tooltip>

        <Tooltip title="Автоматическая генерация (в разработке)">
          <Button
            variant="outlined"
            size="small"
            startIcon={<SmartToy />}
            onClick={handleAutoGenerate}
            disabled={isGenerating}
          >
            Авто-генерация
          </Button>
        </Tooltip>

        <Tooltip title="Копировать транскрибацию">
          <Button
            variant="outlined"
            size="small"
            startIcon={<ContentCopy />}
            onClick={handleCopy}
            disabled={!value}
          >
            Копировать
          </Button>
        </Tooltip>

        <Tooltip title="Очистить транскрибацию">
          <Button
            variant="outlined"
            size="small"
            color="error"
            startIcon={<Clear />}
            onClick={handleClear}
            disabled={!value}
          >
            Очистить
          </Button>
        </Tooltip>
      </Box> */}

      {isGenerating && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
          <Typography variant="caption" color="text.secondary">
            Генерация транскрибации...
          </Typography>
        </Box>
      )}

      <TextInput
        source={source}
        label=""
        fullWidth={fullWidth}
        multiline={multiline}
        minRows={minRows}
        maxRows={maxRows}
        helperText={helperText}
      />

      {value && (
        <Alert severity="info" sx={{ mt: 1 }}>
          Транскрибация содержит {value.length} символов, ~
          {Math.ceil(value.split(' ').length)} слов
        </Alert>
      )}

      {/* Диалог импорта */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Импорт транскрибации</DialogTitle>
        <DialogContent>
          <MuiTextField
            autoFocus
            fullWidth
            multiline
            minRows={8}
            maxRows={15}
            label="Вставьте текст транскрибации"
            value={importText}
            onChange={(e) => setImportText(e.target.value)}
            sx={{ mt: 1 }}
            helperText="Вставьте готовую транскрибацию урока. Текст будет добавлен в поле транскрибации."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Отмена</Button>
          <Button
            onClick={handleImport}
            variant="contained"
            disabled={!importText.trim()}
          >
            Импортировать
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
